import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
import { getInternalAccessCode } from '@/lib/internalAccess';

type Props = {
  children: React.ReactNode;
};

/**
 * Client-side gate for internal-only pages. Wrap sensitive components/routes
 * with this to prevent casual access.
 */
const AnalyticsGate: React.FC<Props> = ({ children }) => {
  const [unlocked, setUnlocked] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    // Ephemeral unlock via query param (no persistence)
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('internal');
      const expected = getInternalAccessCode();
      if (q && expected && q.trim() === expected.trim()) {
        setUnlocked(true);
        // optional: clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete('internal');
        window.history.replaceState({}, document.title, url.toString());
      }
    } catch {
      /* ignore */
    }
  }, []);

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" /> Intern åtkomst
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Denna vy är endast för interna användare. Ange åtkomstkod för att fortsätta.
          </p>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Åtkomstkod"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const ok = (getInternalAccessCode() || '').trim() === (code || '').trim();
                  setUnlocked(ok);
                  setError(ok ? '' : 'Fel kod – försök igen.');
                }
              }}
            />
            <Button
              onClick={() => {
                const ok = (getInternalAccessCode() || '').trim() === (code || '').trim();
                setUnlocked(ok);
                setError(ok ? '' : 'Fel kod – försök igen.');
              }}
            >
              Lås upp
            </Button>
          </div>
          <div className="flex justify-between pt-2">
            <div />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>Till startsidan</Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsGate;


